import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const APP_URL = 'https://console.filemingle.com';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const navLinks = [
    { label: 'Features', href: isHome ? '#features' : '/#features', internal: false },
    { label: 'How It Works', href: isHome ? '#how-it-works' : '/#how-it-works', internal: false },
    { label: 'Tools', href: '/tools', internal: true },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'glass-light shadow-lg shadow-brand-blue/5'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16 sm:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src="/assets/logo_small.png"
              alt="FileMingle"
              className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-105"
            />
            <span
              className={`font-bold text-lg tracking-tight transition-colors duration-300 ${
                scrolled ? 'text-slate-900' : 'text-white'
              }`}
            >
              FileMingle
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7">
            {navLinks.map((link) =>
              link.internal ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors duration-200 hover:text-brand-blue ${
                    scrolled ? 'text-slate-600' : 'text-white/80 hover:text-white'
                  } ${pathname === link.href ? (scrolled ? 'text-brand-blue' : 'text-white') : ''}`}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className={`text-sm font-medium transition-colors duration-200 hover:text-brand-blue ${
                    scrolled ? 'text-slate-600' : 'text-white/80 hover:text-white'
                  }`}
                >
                  {link.label}
                </a>
              )
            )}
            <a
              href={APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-brand-gradient text-white text-sm font-semibold shadow-lg shadow-brand-blue/30 hover:shadow-brand-blue/50 hover:scale-105 transition-all duration-200"
            >
              Launch App
            </a>
          </nav>

          {/* Mobile hamburger */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors duration-200 ${
              scrolled
                ? 'text-slate-700 hover:bg-slate-100'
                : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="md:hidden animate-slide-down pb-5 border-t border-white/10">
            <nav className="flex flex-col gap-1 pt-4">
              {navLinks.map((link) =>
                link.internal ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMenuOpen(false)}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      scrolled
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-white/80 hover:bg-white/10'
                    }`}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMenuOpen(false)}
                    className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      scrolled
                        ? 'text-slate-700 hover:bg-slate-100'
                        : 'text-white/80 hover:bg-white/10'
                    }`}
                  >
                    {link.label}
                  </a>
                )
              )}
              <a
                href={APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 text-center px-5 py-2.5 rounded-full bg-brand-gradient text-white text-sm font-semibold shadow-lg"
              >
                Launch App
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
